import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "../../Style/TableStyle.scss";
import "../../Style/__searchbar.scss";
import "../../Style/__button.scss";
import "./ClientBranchesPage.scss";
import { deleteAlert, errorAlert, handleSuccess } from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { MdOutlinePeopleAlt } from "react-icons/md";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import ClientBranchesModal from "./ClientBranchesModal";
import ClientBranchesViewModal from "./ClientBranchesViewModal";

export default function ClientBranchesPage() {
  const headers = authHeader();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const statesCacheRef = useRef(null);
  const citiesByStateCacheRef = useRef({});

  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [viewBranch, setViewBranch] = useState(null);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);

  const getApiErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.errorMessage) {
      return `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`;
    }

    if (
      typeof error?.response?.data === "string" &&
      error.response.data.trim() !== ""
    ) {
      return error.response.data;
    }

    if (error?.message) {
      return `Gabim: ${error.message}`;
    }

    return fallbackMessage;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "ClientBranches", { headers });
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim i panjohur në marrjen e njësive të klientëve.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const normalizeId = (value) => {
    if (value == null || value === "") {
      return "";
    }

    return String(value);
  };

  const getStates = async () => {
    if (Array.isArray(statesCacheRef.current) && statesCacheRef.current.length > 0) {
      return statesCacheRef.current;
    }

    try {
      const response = await axios.get(ApiLink.API_URL + "states", { headers });
      const nextStates = Array.isArray(response.data) ? response.data : [];
      statesCacheRef.current = nextStates;
      return nextStates;
    } catch {
      return [];
    }
  };

  const getCitiesByState = async (stateId) => {
    const normalizedStateId = normalizeId(stateId);
    if (!normalizedStateId) {
      return [];
    }

    if (Array.isArray(citiesByStateCacheRef.current[normalizedStateId])) {
      return citiesByStateCacheRef.current[normalizedStateId];
    }

    try {
      const response = await axios.get(ApiLink.API_URL + "Cities/getcitybystate", {
        headers,
        params: { stateid: stateId },
      });
      const nextCities = Array.isArray(response.data) ? response.data : [];
      citiesByStateCacheRef.current[normalizedStateId] = nextCities;
      return nextCities;
    } catch {
      return [];
    }
  };

  const enrichBranchLocationNames = async (branch) => {
    if (!branch) {
      return branch;
    }

    const nextBranch = { ...branch };
    const stateId = normalizeId(nextBranch.StateID ?? nextBranch.stateID);
    const cityId = normalizeId(nextBranch.CityID ?? nextBranch.cityID);

    if (!nextBranch.StateName && !nextBranch.stateName && stateId) {
      const states = await getStates();
      const matchedState = states.find(
        (stateEntry) => normalizeId(stateEntry?.StateID) === stateId,
      );
      if (matchedState?.StateName) {
        nextBranch.StateName = matchedState.StateName;
      }
    }

    if (!nextBranch.CityName && !nextBranch.cityName && cityId && stateId) {
      const cities = await getCitiesByState(stateId);
      const matchedCity = cities.find(
        (cityEntry) => normalizeId(cityEntry?.CityID) === cityId,
      );
      if (matchedCity?.CityName) {
        nextBranch.CityName = matchedCity.CityName;
      }
    }

    return nextBranch;
  };

  const fetchBranchDetails = async (item) => enrichBranchLocationNames(item);

  const handleEdit = async (item) => {
    const detailedBranch = await fetchBranchDetails(item);
    setSelectedBranch(detailedBranch);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedBranch(null);
  };

  const handleView = async (item) => {
    const detailedBranch = await fetchBranchDetails(item);
    setViewBranch(detailedBranch);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewBranch(null);
  };

  const handleDelete = async (deleteItemId) => {
    try {
      const response = await axios.delete(
        ApiLink.API_URL + "ClientBranches" + `/${deleteItemId}`,
        { headers },
      );

      const result = response.data;
      if (result.success) {
        handleSuccess("Njësia e klientit është fshirë me sukses");
        fetchData();
      } else {
        deleteAlert(
          "Njësia e klientit nuk mund të fshihet sepse është përdorur në transaksione",
        );
      }
    } catch (error) {
      deleteAlert("Ka ndodhur një gabim gjatë fshirjes së njësisë së klientit.");
    }
  };

  const filteredData = useMemo(() => {
    let result = [...data];

    const quickText = (quickFilter || []).join(" ").trim().toLowerCase();
    if (quickText) {
      result = result.filter((item) =>
        [
          item.ClientBranchName,
          item.AccountName,
          item.ClientBranchIdentificationNumber,
          item.ClientBranchAddress,
          item.StateName,
          item.CityName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(quickText)),
      );
    }

    if (Array.isArray(filterModel?.items)) {
      filterModel.items.forEach((filterItem) => {
        const field = filterItem?.field;
        const value = String(filterItem?.value ?? "").toLowerCase().trim();
        if (!field || value === "") {
          return;
        }

        result = result.filter((item) => {
          const raw = item?.[field];
          return String(raw ?? "").toLowerCase().includes(value);
        });
      });
    }

    if (Array.isArray(sortModel) && sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      if (field && sort) {
        result.sort((a, b) => {
          const aValue = a?.[field];
          const bValue = b?.[field];

          const aComparable = typeof aValue === "number" ? aValue : String(aValue ?? "");
          const bComparable = typeof bValue === "number" ? bValue : String(bValue ?? "");

          if (aComparable < bComparable) {
            return sort === "asc" ? -1 : 1;
          }
          if (aComparable > bComparable) {
            return sort === "asc" ? 1 : -1;
          }
          return 0;
        });
      }
    }

    return result;
  }, [data, filterModel, quickFilter, sortModel]);

  const rowCount = filteredData.length;
  const pagedData = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return filteredData.slice(start, start + paginationModel.pageSize);
  }, [filteredData, paginationModel]);

  const clientsGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 40,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "ClientBranchName",
      headerName: "Emri i Njësisë",
      flex: 2.1,
      minWidth: 210,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "AccountName",
      headerName: "Emri i Klientit",
      flex: 2.0,
      minWidth: 210,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "ClientBranchIdentificationNumber",
      headerName: "Nr. Identifikues",
      flex: 0.9,
      minWidth: 130,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "ClientBranchAddress",
      headerName: "Adresa",
      flex: 1.5,
      minWidth: 170,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Statusi",
      minWidth: 85,
      flex: 0.7,
      headerClassName: "super-app-theme--header",
      sortable: true,
      filterable: true,
      filterType: "select",
      filterOptions: [
        { value: "true", label: "Aktiv" },
        { value: "false", label: "Joaktiv" },
      ],
      renderCell: (params) =>
        params.value ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 20,
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.3px",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#10b981",
                display: "inline-block",
              }}
            />
            Aktiv
          </span>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 20,
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.3px",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#ef4444",
                display: "inline-block",
              }}
            />
            Joaktiv
          </span>
        ),
    },
  ];

  const rows = pagedData.map((item, index) => ({
    id: paginationModel.page * paginationModel.pageSize + index + 1,
    ClientBranchName: item.ClientBranchName,
    AccountName: item.AccountName,
    ClientBranchIdentificationNumber: item.ClientBranchIdentificationNumber,
    ClientBranchAddress: item.ClientBranchAddress,
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.ClientBranchID,
    rowInfo: item,
  }));

  return (
    <div className="Crud__Container CBP__page">
      <ClientBranchesModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        mode="edit"
        initialData={selectedBranch}
        fetchData={fetchData}
      />

      <ClientBranchesViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        branchData={viewBranch}
      />

      <CrudPageHeader
        icon={MdOutlinePeopleAlt}
        title="Lista e Njësive të Klientëve"
        description="Forma e Njësive të Klientëve: Menaxhoni, shtoni, editoni dhe fshini njësitë."
        tutorialUrl="https://www.youtube.com/@Accountree"
        tutorialLabel="Shiko Tutorialin"
        action={<ClientBranchesModal fetchData={fetchData} type="text" mode="insert" />}
        className="CBP__header"
      />

      <ReusableServerDataGrid
        columns={clientsGridHeader}
        rows={rows}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        showViewAction={true}
        showPayment={false}
        actionsMinWidth={90}
        actionsFlex={0.75}
        height={500}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
      />
    </div>
  );
}